<template>
  <div class="edt">
    <v-app-bar
      flat
      color="transparent"
      class="main-appbar"
    >
      <app-bar-nav-icon />
      <div class="d-flex align-center">
        <v-toolbar-title>
          <h1 class="headline font-weight-bold">{{ pageTitle }}
          </h1>
        </v-toolbar-title>
      </div>
      <v-spacer />
      <div>
        <v-btn
          color="primary"
          class="mr-4"
          medium
          @click="onCancel"
          :disabled="form.$busy"
          text
          style="text-transform: none;"
        >Cancel</v-btn>
        <v-btn
          color="primary"
          medium
          @click="onSave"
          :loading="form.$busy"
          style="text-transform: none;"
        >Save</v-btn>
      </div>
    </v-app-bar>
    <v-row class="px-4 py-4">
      <v-col
        lg="8"
        md="6"
        sm="12"
        xs="12"
      >
        <v-card
          outlined
          class="px-6 py-8"
        >

          <v-textarea
            v-model="form.logo_url"
            outlined
            label="URL"
            auto-grow
            rows="2"
            :error-messages="replace(head(form.$getError('logo_url')), 'The logo url', 'URL')"
          >
          </v-textarea>

          <image-file-input-viewer
            label="Banner Logo"
            v-model="form.logo"
            :error-messages="replace(head(form.$getError('logo')), 'The logo field', 'Banner logo')"
          />

          <us-states-selector
            v-model="form.us_states_id"
            :isMultiple="true"
            :error-messages="replace(head(form.$getError('us_states_id')), 'The us states id', 'This State')"
          />

          <v-text-field
            v-model="form.max_width"
            outlined
            label="Max Width (px)"
            :error-messages="form.$getError('max_width')"
            type="number"
          />

          <color-picker
            label="Background Color"
            v-bind:color.sync="form.background_color"
            :error-messages="form.$getError('background_color')"
          />

          <v-textarea
            v-model="form.heading"
            outlined
            label="Heading Text"
            :error-messages="form.$getError('heading')"
          >
          </v-textarea>

          <color-picker
            label="Heading Text Color"
            v-bind:color.sync="form.heading_color"
            :error-messages="form.$getError('heading_color')"
          />

          <v-textarea
            v-model="form.description"
            outlined
            label="T&Cs Text"
            :error-messages="replace(head(form.$getError('description')), 'The description field', 'T&Cs Text')"
          >
          </v-textarea>

          <v-text-field
            v-model="form.description_font_size"
            outlined
            label="T&Cs Text Size"
            type="number"
            :error-messages="form.$getError('description_font_size')"
          ></v-text-field>

          <color-picker
            label="T&Cs Text Color"
            v-bind:color.sync="form.description_color"
            :error-messages="form.$getError('description_color')"
          />

          <v-text-field
            v-model="form.button_text"
            outlined
            label="Button Text"
            :error-messages="form.$getError('button_text')"
          ></v-text-field>

          <v-text-field
            v-model="form.button_text_size"
            outlined
            label="Button Text Size"
            type="number"
            :error-messages="form.$getError('button_text_size')"
          ></v-text-field>

          <color-picker
            label="Button Text Color"
            v-bind:color.sync="form.button_text_color"
            :error-messages="form.$getError('button_text_color')"
          />

          <color-picker
            label="Button Color"
            v-bind:color.sync="form.button_color"
            tempColor="#000000"
            :error-messages="form.$getError('button_color')"
          />

        </v-card>
      </v-col>
      <v-col
        lg="4"
        md="6"
        sm="12"
      >
        <v-card
          outlined
          class="px-6"
        >
          <div class="d-flex flex-row justify-space-between">
            <label class="subtitle-1 grey--text pt-1 mt-4">Show on Home</label>
            <v-switch
              color="primary"
              v-model="form.is_show_home"
              :error-messages="form.$getError('is_show_home')"
            />
          </div>

          <div class="d-flex flex-row justify-space-between">
            <label class="subtitle-1 grey--text pt-1 mt-4">Show on News Page</label>
            <v-switch
              color="primary"
              v-model="form.is_show_news"
              :error-messages="form.$getError('is_show_news')"
            />
          </div>
        </v-card>

        <bookmaker-code
          v-bind:bookmakerCode.sync="form.bookmaker_code"
          :errorMessages="form.$getError('bookmaker_code')"
        />

        <published-date-selector
          pageTitle="Publish Date"
          class="mt-6"
          v-model="form.published_date"
          :errorMessages="form.$getError('published_date')"
        />

        <published-date-selector
          class="mt-6"
          pageTitle="Start Date"
          v-model="form.start_date"
          :errorMessages="form.$getError('start_date')"
        />

        <published-date-selector
          class="mt-6"
          pageTitle="End Date"
          v-model="form.end_date"
          :errorMessages="form.$getError('end_date')"
        />

        <image-alt-text v-bind:imageAltText.sync="form.image_alt_text" />

      </v-col>
    </v-row>
    <v-overlay
      absolute
      :value="updating"
    >
      <v-dialog
        v-model="updating"
        hide-overlay
        persistent
        width="300"
      >
        <v-card
          color="primary"
          dark
        >
          <v-card-text>
            {{ loading_message }}
            <v-progress-linear
              indeterminate
              color="white"
              class="mb-0"
            ></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-overlay>
  </div>
</template>

<script>
import Form from '@/utils/form'
import get from 'lodash/get'
import head from 'lodash/head'
import replace from 'lodash/replace'
import Banner from '@/models/Banner'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import { mapActions } from 'vuex'
import ColorPicker from '@/components/ColorPicker'
import ImageFileInputViewer from '@/components/ImageFileInputViewer'
import PublishedDateSelector from '@/views/Home/Articles/shared/PublishedDateSelector'
import dayjs from 'dayjs'
import has from 'lodash/has'
import isNull from 'lodash/isNull'
import ImageAltText from '@/components/ImageAltText'
import UsStatesSelector from '@/components/UsStatesSelector'
import map from 'lodash/map'
import BookmakerCode from '@/components/BookmakerCode'


const initialData = {
  name: null,
  background_color: '#000000',
  logo: null,
  end_date: null,
  start_date: null,
  published_date: null,
  button_color: '#000000',
  button_text_color: '#FFFFFF',
  button_text_size: null,
  button_text: null,
  description_color: '#FFFFFF',
  description: null,
  heading_color: '#FFFFFF',
  heading: null,
  logo_url: null,
  show_banner: false,
  image_alt_text: null,
  description_font_size: null,
  is_show_news: true,
  is_show_home: true,
  us_states_id: [],
  max_width: null,
  bookmaker_code: null
}

export default {
  data () {
    return {
      loading: false,
      updating: false,
      loading_message: 'Loading',
      snackbar: {
        show: false,
        message: null,
        color: 'success'
      },
      loadingTags: false,
      form: new Form({
        ...initialData
      })
    }
  },
  props: {
    banner: {
      type: [Banner, Object],
      default () {
        return {}
      }
    },
    pageTitle: {
      type: String,
      default: 'Adhesive Banner'
    },
    redirectCancelTo: {
      type: [String, Object],
      default () {
        return {
          name: 'adhesive-banners'
        }
      }
    },
    redirectSaveTo: {
      type: [String, Object],
      default () {
        return {
          name: 'adhesive-banners'
        }
      }
    }
  },

  components: {
    AppBarNavIcon,
    ColorPicker,
    ImageFileInputViewer,
    PublishedDateSelector,
    ImageAltText,
    UsStatesSelector,
    BookmakerCode
  },

  methods: {
    head,
    replace,
    ...mapActions({
      updateRecord: 'Banner/update'
    }),

    onCancel () {
      this.$router.replace(this.redirectCancelTo)
    },

    async onSave () {
      try {
        this.form.$busy = true
        this.form.$clearErrors()
        const data = this.form.$data()

        data.local_date = dayjs().format()

        if (has(data, 'end_date') && !isNull(data.end_date)) {
          data.end_date = dayjs(data.end_date).format()
        }

        if (has(data, 'start_date') && !isNull(data.start_date)) {
          data.start_date = dayjs(data.start_date).format()
        }

        if (has(data, 'published_date') && !isNull(data.published_date)) {
          data.published_date = dayjs(data.published_date).format()
        }

        if (this.isModeUpdate) {
          data._method = 'put'

          if (data.logo && data.logo.id) { this.$delete(data, 'logo') }
        }

        const banner = new Banner(data)
        await banner.save()

        this.$router.replace(this.redirectSaveTo)
      } catch (error) {
        if (get(error, 'response.status') === 422) {
          if (get(error, 'response.data.errors') !== null) {
            this.form.$setErrors(get(error, 'response.data.errors'))
          } else {
            this.showSnackbar('Something went wrong.', 'error')
          }
        } else {
          this.showSnackbar(error, 'error')
        }
      } finally {
        this.form.$busy = false
      }
    },
    showSnackbar (message, color = 'success') {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    }

  },

  watch: {
    banner: {
      immediate: true,
      handler (val) {
        if (val) {
          this.form = new Form({
            ...initialData,
            ...val,
          })

          if (val.logo) {
            this.form.image_alt_text = val.logo.custom_properties.alt_text
          }

          if (has(this.form, 'end_date') && !isNull(this.form.end_date)) {
            this.form.end_date = dayjs(new Date(this.form.end_date + " UTC")).format('YYYY-MM-DD HH:mm:ss')
          }

          if (has(this.form, 'start_date') && !isNull(this.form.start_date)) {
            this.form.start_date = dayjs(new Date(this.form.start_date + " UTC")).format('YYYY-MM-DD HH:mm:ss')
          }

          if (has(this.form, 'published_date') && !isNull(this.form.published_date)) {
            this.form.published_date = dayjs(new Date(this.form.published_date + " UTC")).format('YYYY-MM-DD HH:mm:ss')
          }

          this.form.us_states_id = map(get(val, 'states', []), item => {
            return item.id

          })
        }
      }
    }
  },

  computed: {
    isModeUpdate () {
      return get(this.form, 'id', null) !== null
    }
  }
}
</script>
